import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import {
  registerUrl,
  completeProfilePath,
  runningCompleteProfilePath,
  hikingCompleteProfilePath,
  clubHomePath,
} from '../utils/paths';
import {
  listClubSports,
  getClubSport,
  getClubSportLabel,
  normalizeClubSport,
} from '../config/clubSports';
import MembershipStatusBanner, {
  useMyClubMemberships,
  getMembershipStatusForClub,
} from './MembershipStatusBanner';
import './ClubsList.css';
import './MembershipStatusBanner.css';

const ClubsList = ({
  forcedSport = null,
  registerPath = null,
} = {}) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const [clubs, setClubs] = useState([]);
  const [pendingClubs, setPendingClubs] = useState([]);
  const [player, setPlayer] = useState(null);
  const [runnerProfile, setRunnerProfile] = useState(null);
  const [hikerProfile, setHikerProfile] = useState(null);
  const [hasPendingPlayer, setHasPendingPlayer] = useState(false);
  const [playerLoaded, setPlayerLoaded] = useState(false);
  const [runnerProfileLoaded, setRunnerProfileLoaded] = useState(false);
  const [hikerProfileLoaded, setHikerProfileLoaded] = useState(false);
  const [playerClubs, setPlayerClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const successRef = useRef(null);

  const sports = listClubSports();
  const activeSport = forcedSport
    ? normalizeClubSport(forcedSport)
    : normalizeClubSport(searchParams.get('sport') || sports[0]?.id);

  const { memberships, pendingMemberships, reload: reloadMemberships } = useMyClubMemberships(
    isAuthenticated ? user?.id : null
  );

  const setActiveSport = (sportId) => {
    if (forcedSport) return;
    const next = normalizeClubSport(sportId);
    setSearchParams({ sport: next }, { replace: true });
  };

  const loadPlayer = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setPlayer(null);
      setHasPendingPlayer(false);
      setPlayerLoaded(true);
      return;
    }

    try {
      const allPlayers = await api.getAllPlayers(user?.id);
      const userPlayer = allPlayers.find((p) => p.userId === user.id);
      if (userPlayer) {
        setPlayer(userPlayer);
        setHasPendingPlayer(false);
      } else {
        setPlayer(null);
        try {
          const pendingPlayers = await api.getPendingPlayers();
          const pendingPlayer = pendingPlayers.find((p) => p.userId === user.id);
          setHasPendingPlayer(!!pendingPlayer);
        } catch {
          setHasPendingPlayer(false);
        }
      }
    } catch {
      setPlayer(null);
      setHasPendingPlayer(false);
    } finally {
      setPlayerLoaded(true);
    }
  }, [isAuthenticated, user]);

  const loadRunnerProfile = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setRunnerProfile(null);
      setRunnerProfileLoaded(true);
      return;
    }

    try {
      const data = await api.getMyRunnerProfile(user.id);
      setRunnerProfile(data);
    } catch {
      setRunnerProfile(null);
    } finally {
      setRunnerProfileLoaded(true);
    }
  }, [isAuthenticated, user]);

  const loadHikerProfile = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setHikerProfile(null);
      setHikerProfileLoaded(true);
      return;
    }

    try {
      const data = await api.getMyHikerProfile(user.id);
      setHikerProfile(data);
    } catch {
      setHikerProfile(null);
    } finally {
      setHikerProfileLoaded(true);
    }
  }, [isAuthenticated, user]);

  const loadClubs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await api.getApprovedClubs();
      setClubs(Array.isArray(data) ? data : []);
      if (isAuthenticated && user?.id) {
        try {
          const pending = await api.getPendingClubsForUser(user.id);
          setPendingClubs(Array.isArray(pending) ? pending : []);
        } catch {
          setPendingClubs([]);
        }
      } else {
        setPendingClubs([]);
      }
    } catch (err) {
      setError(err.message || 'Failed to load clubs');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.id]);

  const loadPlayerClubs = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setPlayerClubs([]);
      return;
    }
    try {
      const myClubs = await api.getMyClubs(user.id);
      setPlayerClubs(myClubs || []);
    } catch {
      setPlayerClubs([]);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    loadPlayer();
    loadRunnerProfile();
    loadHikerProfile();
  }, [loadPlayer, loadRunnerProfile, loadHikerProfile]);

  const sportProfilesLoaded = playerLoaded && runnerProfileLoaded && hikerProfileLoaded;

  useEffect(() => {
    if (sportProfilesLoaded || !isAuthenticated) {
      loadClubs();
    }
  }, [sportProfilesLoaded, isAuthenticated, location.pathname, loadClubs]);

  useEffect(() => {
    if (sportProfilesLoaded && isAuthenticated) {
      loadPlayerClubs();
    }
  }, [sportProfilesLoaded, isAuthenticated, loadPlayerClubs]);

  useEffect(() => {
    if (success && successRef.current) {
      successRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [success]);

  const clubsForSport = useMemo(
    () => clubs.filter((club) => normalizeClubSport(club.sport) === activeSport),
    [clubs, activeSport]
  );

  const pendingForSport = useMemo(
    () => pendingClubs.filter((club) => normalizeClubSport(club.sport) === activeSport),
    [pendingClubs, activeSport]
  );

  const activeSportMeta = getClubSport(activeSport);

  const handleRequestToJoin = async (clubId) => {
    const membershipStatus = getMembershipStatusForClub(memberships, clubId);
    if (membershipStatus === 'PENDING') {
      setError('You already requested to join this club. Please wait for the club admin to approve your request.');
      return;
    }

    const club = clubs.find((c) => c.id === clubId);
    const clubSport = normalizeClubSport(club?.sport);
    const sportMeta = getClubSport(clubSport);

    let playerId = player?.id;
    if (sportMeta.requiresRunnerProfile) {
      playerId = runnerProfile?.membershipPlayerId;
      if (!playerId && runnerProfile) {
        try {
          const refreshed = await api.getMyRunnerProfile(user.id);
          setRunnerProfile(refreshed);
          playerId = refreshed?.membershipPlayerId;
        } catch {
          playerId = null;
        }
      }
    } else if (sportMeta.requiresHikerProfile) {
      playerId = hikerProfile?.membershipPlayerId;
      if (!playerId && hikerProfile) {
        try {
          const refreshed = await api.getMyHikerProfile(user.id);
          setHikerProfile(refreshed);
          playerId = refreshed?.membershipPlayerId;
        } catch {
          playerId = null;
        }
      }
    } else if (!playerId) {
      try {
        const profile = await api.getMyPlayerProfile(user.id);
        playerId = profile?.id;
      } catch {
        setError('You need to register as a player first and be approved by the system admin');
        return;
      }
    }

    if (!playerId) {
      if (sportMeta.requiresRunnerProfile) {
        setError('Complete your running profile before requesting to join a club.');
      } else if (sportMeta.requiresHikerProfile) {
        setError('Complete your hiking profile before requesting to join a club.');
      } else {
        setError('You need to register as a player first and be approved by the system admin');
      }
      return;
    }

    try {
      setError('');
      setSuccess('');
      await api.requestClubMembership(clubId, playerId);
      await reloadMemberships();
      const clubName = clubs.find((c) => c.id === clubId)?.name || 'the club';
      setSuccess(`Your request to join ${clubName} was sent. The club admin will review it — no need to request again.`);
    } catch (err) {
      const message = err.message || 'Failed to request membership';
      if (message.toLowerCase().includes('already pending')) {
        await reloadMemberships();
        setError('You already requested to join this club. Please wait for the club admin to approve your request.');
      } else {
        setError(message);
      }
    }
  };

  const renderMembershipStatus = (clubId) => {
    const status = getMembershipStatusForClub(memberships, clubId);
    const isClubAdmin = playerClubs.some((pc) => pc.id === clubId);

    if (isClubAdmin || status === 'APPROVED') {
      return (
        <span className="club-membership-badge club-membership-badge--approved">Member</span>
      );
    }
    if (status === 'PENDING') {
      return (
        <span className="club-membership-badge club-membership-badge--pending">
          Request sent — awaiting club admin
        </span>
      );
    }
    if (status === 'REJECTED') {
      return (
        <span className="club-membership-badge club-membership-badge--rejected">
          Request declined
        </span>
      );
    }
    return null;
  };

  const hasSportProfile = (sportId) => {
    const meta = getClubSport(sportId);
    if (meta.requiresRunnerProfile) return Boolean(runnerProfile);
    if (meta.requiresHikerProfile) return Boolean(hikerProfile);
    if (meta.requiresPlayerProfile) return Boolean(player);
    return Boolean(player || runnerProfile || hikerProfile);
  };

  const canRequestToJoin = (club) => {
    if (!isAuthenticated) return false;
    const sportMeta = getClubSport(club.sport);
    if (!hasSportProfile(club.sport)) return false;
    if (sportMeta.requiresPlayerProfile && hasPendingPlayer) return false;
    if (club.allowNewMembers === false) return false;
    if (playerClubs.some((pc) => pc.id === club.id)) return false;

    const status = getMembershipStatusForClub(memberships, club.id);
    return !status || status === 'REJECTED';
  };

  if (loading) {
    return <div className="loading">Loading clubs...</div>;
  }

  return (
    <div className="clubs-list-page">
      <div className="card">
        <h2>Clubs</h2>
        <p className="clubs-list-page__intro">
          {forcedSport ? (
            <>
              Browse <strong>{activeSportMeta.label}</strong> clubs. Routes, practices, and votes
              live in the Running app.
            </>
          ) : (
            <>
              Sports stay separate — browse <strong>{activeSportMeta.label}</strong> clubs here. Switch
              sport to see a different list.
            </>
          )}
        </p>

        {!forcedSport && (
        <div className="clubs-sport-tabs" role="tablist" aria-label="Club sports">
          {sports.map((sport) => {
            const count = clubs.filter((club) => normalizeClubSport(club.sport) === sport.id).length;
            const isActive = activeSport === sport.id;
            return (
              <button
                key={sport.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`clubs-sport-tabs__tab${isActive ? ' is-active' : ''}`}
                onClick={() => setActiveSport(sport.id)}
              >
                {sport.label}
                <span className="clubs-sport-tabs__count">{count}</span>
              </button>
            );
          })}
        </div>
        )}

        {!forcedSport && activeSport === 'RUNNING' && (
          <p className="clubs-list-page__intro" style={{ marginTop: '0.75rem' }}>
            Running clubs have a dedicated app for routes and group practices.{' '}
            <Link to="/running/clubs/list">Open Running →</Link>
          </p>
        )}

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '1rem' }}>
          <button onClick={loadClubs} className="btn btn-secondary">
            Refresh
          </button>
          {registerPath && (
            <Link to={registerPath} className="btn btn-primary">
              Register club
            </Link>
          )}
          {forcedSport === 'RUNNING' && (
            <Link to="/soccer/clubs/list?sport=SOCCER" className="btn btn-secondary">
              Soccer clubs
            </Link>
          )}
        </div>
      </div>

      {isAuthenticated && pendingMemberships.length > 0 && (
        <MembershipStatusBanner
          pendingMemberships={pendingMemberships}
          showPlayerPendingNote={hasPendingPlayer}
        />
      )}

      {error && <div className="alert alert-error">{error}</div>}
      {success && (
        <div ref={successRef} className="alert alert-success clubs-list-success" role="status">
          <strong>Request confirmed</strong>
          <p style={{ margin: '0.35rem 0 0' }}>{success}</p>
        </div>
      )}

      {pendingForSport.length > 0 && (
        <div className="card" style={{ marginBottom: '1.5rem', borderLeft: '4px solid #ffc107' }}>
          <h3>
            Your {activeSportMeta.label.toLowerCase()} clubs pending approval ({pendingForSport.length})
          </h3>
          <p style={{ color: '#856404', marginBottom: '1rem' }}>
            New clubs appear here until a system admin approves them.
          </p>
          <div className="clubs-list" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {pendingForSport.map((club) => (
              <div key={club.id} className="club-card" style={{ opacity: 0.95 }}>
                {club.logoUrl && (
                  <img
                    src={club.logoUrl}
                    alt={club.name}
                    className="club-logo"
                    style={{ maxWidth: '48px', maxHeight: '48px' }}
                  />
                )}
                <div className="club-info" style={{ flex: 1 }}>
                  <h3 style={{ margin: 0 }}>{club.name}</h3>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: '#5a6b62' }}>
                    {getClubSportLabel(club.sport)}
                  </p>
                  {club.description && (
                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem' }}>{club.description}</p>
                  )}
                  <span className="badge badge-warning" style={{ marginTop: '0.5rem' }}>
                    Pending approval
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {clubsForSport.length === 0 && pendingForSport.length === 0 ? (
        <div className="empty-state">
          <h3>No {activeSportMeta.label.toLowerCase()} clubs yet</h3>
          <p>Register a {activeSportMeta.label.toLowerCase()} club or switch to another sport.</p>
        </div>
      ) : clubsForSport.length === 0 ? null : (
        <div className="card">
          <h3>
            {activeSportMeta.label} clubs ({clubsForSport.length})
          </h3>
          <p className="clubs-list-page__sport-note">{activeSportMeta.description}</p>
          <div className="clubs-list">
            {clubsForSport.map((club) => {
              const sportMeta = getClubSport(club.sport);
              const membershipStatus = getMembershipStatusForClub(memberships, club.id);
              const membershipBadge = isAuthenticated ? renderMembershipStatus(club.id) : null;

              return (
                <div key={club.id} className="club-card">
                  {club.logoUrl && (
                    <img src={club.logoUrl} alt={club.name} className="club-logo" />
                  )}
                  <div className="club-info">
                    <h3>{club.name}</h3>
                    <p className="club-info__sport">{getClubSportLabel(club.sport)}</p>
                    {club.description && <p>{club.description}</p>}
                    <div className="club-meta">
                      {club.allowNewMembers === false && (
                        <span className="badge badge-warning">Not accepting new members</span>
                      )}
                    </div>
                  </div>
                  <div className="club-actions">
                    {(hasSportProfile(club.sport) ||
                      (isAuthenticated &&
                        user &&
                        (user.roles?.includes('ADMIN') || user.roles?.includes('CLUB_CREATOR')))) && (
                      <Link to={clubHomePath(club.id, club.sport)} className="btn btn-secondary">
                        View Details
                      </Link>
                    )}
                    {canRequestToJoin(club) && (
                      <button
                        onClick={() => handleRequestToJoin(club.id)}
                        className="btn btn-primary"
                      >
                        {membershipStatus === 'REJECTED' ? 'Request Again' : sportMeta.joinCta}
                      </button>
                    )}
                    {membershipBadge}
                    {isAuthenticated &&
                      !player &&
                      hasPendingPlayer &&
                      sportMeta.requiresPlayerProfile &&
                      membershipStatus !== 'PENDING' && (
                        <div
                          className="alert alert-warning"
                          style={{
                            padding: '0.75rem',
                            fontSize: '0.9rem',
                            backgroundColor: '#fff3cd',
                            border: '1px solid #ffc107',
                            borderRadius: '6px',
                            color: '#856404',
                            margin: '0.5rem 0',
                          }}
                        >
                          <strong>Player profile pending</strong>
                          <p style={{ margin: '0.25rem 0 0 0' }}>
                            Your player profile is pending system admin approval. You can request to
                            join clubs once approved.
                          </p>
                        </div>
                      )}
                    {isAuthenticated && !hasSportProfile(club.sport) && sportMeta.requiresRunnerProfile && (
                      <Link to={runningCompleteProfilePath(club.id)} className="btn btn-primary">
                        Complete running profile
                      </Link>
                    )}
                    {isAuthenticated && !hasSportProfile(club.sport) && sportMeta.requiresHikerProfile && (
                      <Link to={hikingCompleteProfilePath(club.id)} className="btn btn-primary">
                        Complete hiking profile
                      </Link>
                    )}
                    {isAuthenticated && !player && !hasPendingPlayer && sportMeta.requiresPlayerProfile && (
                      <Link to={completeProfilePath(club.id)} className="btn btn-primary">
                        Complete player profile
                      </Link>
                    )}
                    {!isAuthenticated && (
                      <Link to={registerUrl(club.id)} className="btn btn-primary">
                        {sportMeta.guestCta}
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClubsList;
